import type { SocialPage } from "@nlc/shared-types";

import { Trash2 } from "lucide-react";
import { useState } from "react";
import { TableBody } from "react-aria-components";

import { Button } from "@/components/button";
import { ConfirmationModal } from "@/components/confirmation-modal";
import { Loader } from "@/components/Loader";
import { Cell, Column, Row, Table, TableHeader } from "@/components/table";
import { toast } from "@/components/toast/toast-queue";

import { useDeleteSocialPage } from "../hooks/useDeleteSocialPage";
import { useSocialPages } from "../hooks/useSocialPages";
import { SOCIAL_PLATFORM_LABELS } from "./socialPlatformLabels";

export function MetaAccountsTable() {
  const { data, isLoading } = useSocialPages();
  const deletePage = useDeleteSocialPage();
  const [pageToDelete, setPageToDelete] = useState<null | SocialPage>(null);

  if (isLoading) return <Loader className="py-20" />;

  function confirmDelete() {
    if (!pageToDelete) return;

    deletePage.mutate(pageToDelete.id, {
      onError: () => {
        toast.add(
          { title: "Could not remove the page", type: "error" },
          { timeout: 5000 },
        );
      },
      onSettled: () => setPageToDelete(null),
    });
  }

  return (
    <>
      <div className="rounded-xl border border-border-secondary bg-bg-primary">
        <Table aria-label="Meta accounts">
          <TableHeader>
            <Column defaultWidth="3fr" id="page" isRowHeader minWidth={220}>
              Page
            </Column>
            <Column defaultWidth="2fr" id="user" minWidth={160}>
              User
            </Column>
            <Column defaultWidth="2fr" id="brand" minWidth={160}>
              Brand related
            </Column>
            <Column defaultWidth={80} id="actions">
              Actions
            </Column>
          </TableHeader>
          <TableBody
            items={data?.pages ?? []}
            renderEmptyState={() => (
              <p className="p-3xl text-sm text-text-tertiary">
                No pages linked to brands yet.
              </p>
            )}
          >
            {(page) => (
              <Row>
                <Cell>
                  <div className="flex items-center gap-md py-md">
                    {page.pictureUrl && (
                      <img
                        alt=""
                        className="size-8 rounded-full"
                        src={page.pictureUrl}
                      />
                    )}
                    <div className="flex flex-col">
                      <span className="font-medium text-text-primary">
                        {page.name ?? page.username}
                      </span>
                      <span>{SOCIAL_PLATFORM_LABELS[page.platform]}</span>
                    </div>
                  </div>
                </Cell>
                <Cell>{page.user.name}</Cell>
                <Cell>{page.brand.name}</Cell>
                <Cell>
                  <Button
                    aria-label={`Remove ${page.name ?? page.username}`}
                    isIconOnly
                    onPress={() => setPageToDelete(page)}
                    size="sm"
                    variant="unstyled"
                  >
                    <Trash2 className="text-text-error-primary" size={16} />
                  </Button>
                </Cell>
              </Row>
            )}
          </TableBody>
        </Table>
      </div>

      <ConfirmationModal
        confirmButtonText="Remove"
        icon={<Trash2 size={20} />}
        isConfirmLoading={deletePage.isPending}
        isOpen={pageToDelete !== null}
        onConfirm={confirmDelete}
        onOpenChange={(isOpen) => {
          if (!isOpen) setPageToDelete(null);
        }}
        text={`${pageToDelete?.name ?? pageToDelete?.username} will be unlinked from ${pageToDelete?.brand.name}.`}
        title="Remove page"
      />
    </>
  );
}
